import React, { useState, useMemo, useCallback } from "react";
import VideoDetailView from "./video-detail-view";
import { VideoDetail } from "./types";

export const YoutubeViewer = () => {
    const [url, setUrl] = useState("");
    const [videoId, setVideoId] = useState<string | null>(null);
    const [videoDetails, setVideoDetails] = useState<VideoDetail | null>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [errorDetails, setErrorDetails] = useState<string | null>(null);

    // Function to extract video ID from various YouTube URL formats
    const extractVideoId = (inputUrl: string): string | null => {
        if (!inputUrl) return null;
        // Allow pasting just the ID
        if (/^[a-zA-Z0-9_-]{11}$/.test(inputUrl)) {
            return inputUrl;
        }
        try {
            const urlObj = new URL(inputUrl);
            if (urlObj.hostname === "youtu.be") {
                return urlObj.pathname.slice(1).split("?")[0]; // Handle query params after ID
            }
            if (urlObj.hostname.includes("youtube.com")) {
                if (urlObj.pathname === "/watch") {
                    return urlObj.searchParams.get("v");
                }
                if (urlObj.pathname.startsWith("/embed/")) {
                    return urlObj.pathname.split("/")[2].split("?")[0];
                }
                if (urlObj.pathname.startsWith("/shorts/")) {
                    return urlObj.pathname.split("/")[2].split("?")[0];
                }
            }
        } catch (e) {
            // Fallback for URLs without protocol, etc.
            const shortMatch = inputUrl.match(/youtu\.be\/([^?&\s/]+)/);
            if (shortMatch) return shortMatch[1];
            const longMatch = inputUrl.match(/youtube\.com.*[?&]v=([^&/\s]+)/);
            if (longMatch) return longMatch[1];
            const embedMatch = inputUrl.match(/youtube\.com\/embed\/([^?&\s/]+)/);
            if (embedMatch) return embedMatch[1];
            const shortsMatch = inputUrl.match(/youtube\.com\/shorts\/([^?&\s/]+)/);
            if (shortsMatch) return shortsMatch[1];
            return null;
        }
        // Fallback regex checks (kept from previous version)
        const shortMatch = inputUrl.match(/youtu\.be\/([^?&\s/]+)/);
        if (shortMatch) return shortMatch[1];
        const longMatch = inputUrl.match(/youtube\.com.*[?&]v=([^&/\s]+)/);
        if (longMatch) return longMatch[1];
        const embedMatch = inputUrl.match(/youtube\.com\/embed\/([^?&\s/]+)/);
        if (embedMatch) return embedMatch[1];
        const shortsMatch = inputUrl.match(/youtube\.com\/shorts\/([^?&\s/]+)/);
        if (shortsMatch) return shortsMatch[1];

        return null;
    };

    // --- Backend API Call Simulation ---
    const fetchVideoDetails = useCallback(async (id: string) => {
        setIsLoadingDetails(true);
        setErrorDetails(null);
        setVideoDetails(null);

        try {
            const workerUrl = `https://api-worker.erashidi.workers.dev/api/youtube/info?videoId=${id}`;
            const response = await fetch(workerUrl);

            if (!response.ok) {
                let errorMsg = `Error fetching details: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMsg += ` - ${errorData.error || "Unknown worker error"}`;
                } catch (e) {
                    /* Ignore if response is not JSON */
                }
                throw new Error(errorMsg);
            }

            const data: VideoDetail = await response.json();
            setVideoDetails(data);
        } catch (error) {
            setErrorDetails(error instanceof Error ? error.message : "خطا در دریافت اطلاعات.");
        } finally {
            setIsLoadingDetails(false);
        }
    }, []);
    // --- End Backend API Call Simulation ---

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newUrl = event.target.value;
        setUrl(newUrl);
        const extractedId = extractVideoId(newUrl);
        setVideoId(extractedId);
        if (extractedId) {
            fetchVideoDetails(extractedId);
        } else {
            setVideoDetails(null);
            setErrorDetails(null);
        }
    };

    // Use useMemo to create the embed URL only when videoId changes
    const embedUrl = useMemo(() => {
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }, [videoId]);

    return (
        <div className="w-full h-full bg-gray-100 flex flex-col">
            <div className="w-full h-10 bg-gray-200 flex items-center px-4 shrink-0 border-b border-gray-300">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <input
                    type="text"
                    value={url}
                    onChange={handleUrlChange}
                    placeholder="آدرس یا ID ویدیوی یوتیوب را وارد کنید"
                    className="flex-1 h-6 bg-white rounded-full mx-2 px-4 text-sm border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className="w-2/3 h-full flex items-center justify-center bg-black p-1">
                    {embedUrl ? (
                        <iframe
                            width="100%"
                            height="100%"
                            src={embedUrl}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen></iframe>
                    ) : (
                        <div className="text-center text-gray-500">
                            <img src="/icons/youtube-large.svg" alt="Youtube" className="w-24 h-24 mx-auto mb-4 opacity-60" />
                            <div>برای مشاهده ویدیو، آدرس آن را در نوار بالا وارد کنید.</div>
                        </div>
                    )}
                </div>

                <div className="w-1/3 h-full bg-white border-l border-gray-300 overflow-y-auto">
                    {isLoadingDetails && (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">در حال بارگذاری جزئیات...</div>
                        </div>
                    )}

                    {errorDetails && (
                        <div className="p-4">
                            <div className="text-red-500 bg-red-100 p-2 rounded">{errorDetails}</div>
                        </div>
                    )}

                    {videoDetails && !isLoadingDetails && <VideoDetailView video={videoDetails} videoId={videoId || ""} />}

                    {!videoId && !isLoadingDetails && !errorDetails && (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-sm text-gray-400 text-center">برای مشاهده جزئیات، آدرس ویدیو را وارد کنید.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
